import { ValueGetterParams } from "ag-grid-community";
import { Item } from "constants/product-collection";
import { AllocationBagType, forEachAllocationBag, getAllocationBagValue } from "helpers/allocation-dimension";
import { get } from "lodash";
import { generateDoorMap, getMetricValuesFromOldBagData } from "./formula-helpers";

/**
 * Formulas for Metrics
 *
 * @param factoryOptions
 *  - provided to `metricsValueGetterCreator`
 *  - untyped, easily allow for custom inputs needed for calculation
 * @param getterParams
 *  - default getterParams from AgGrid
 *  - has context and data etc to facilitate calculation
 * @returns
 *  - a numerical caculated metrics value
 */
export const UNITS = ({ factoryOptions, getterParams }) => {
    const { data, context }: ValueGetterParams = getterParams;
    const { allocationBagRef } = context;
    const { deliveryId, doors, useSize, useAllSizes } = factoryOptions;
    const doorsMap = generateDoorMap(doors);

    /**
     * Item Rows / New allocation bag
     */
    if (allocationBagRef?.allocationBag) {
        const itemId = get(data, Item.FIELDS.ID);
        const allocationBag = allocationBagRef?.allocationBag;
        let total = 0;

        if (deliveryId && deliveryId !== "") {
            for (const door of doors) {
                if (useSize) {
                    const { size } = data;
                    total +=
                        getAllocationBagValue(AllocationBagType.allocations, allocationBag, itemId, [
                            deliveryId,
                            door.id,
                            size,
                        ]) ?? 0;
                } else {
                    total +=
                        getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, [deliveryId, door.id]) ??
                        0;
                }
            }
        } else {
            if (useSize) {
                const { size } = data;
                forEachAllocationBag(allocationBag, itemId, AllocationBagType.allocations, (dimensions, value) => {
                    const numValue = parseInt(value || "0", 10);
                    if (deliveryId === null) {
                        // Case 1: Grand Total Cols
                        if (doorsMap.has(dimensions[1]) && (size === dimensions[2] || useAllSizes)) {
                            total += numValue;
                        }
                    } else if (deliveryId === "") {
                        // Case 2: Retailer without deliveries
                        if (doorsMap.has(dimensions[0]) && (size === dimensions[1] || useAllSizes)) {
                            total += numValue;
                        }
                    } else {
                        // Case 3: All others, normal delivery & door
                        if (
                            dimensions[0] === deliveryId &&
                            doorsMap.has(dimensions[1]) &&
                            (size === dimensions[2] || useAllSizes)
                        ) {
                            total += numValue;
                        }
                    }
                });
            } else {
                forEachAllocationBag(allocationBag, itemId, AllocationBagType.bulks, (dimensions, value) => {
                    const numValue = parseInt(value || "0", 10);
                    if (deliveryId === null) {
                        // Case 1: Grand Total Cols
                        if (doorsMap.has(dimensions[1])) {
                            total += numValue;
                        }
                    } else if (deliveryId === "") {
                        // Case 2: Retailer without deliveries
                        if (doorsMap.has(dimensions[0])) {
                            total += numValue;
                        }
                    } else {
                        // Case 3: All others, normal delivery & door
                        if (dimensions[0] === deliveryId && doorsMap.has(dimensions[1])) {
                            total += numValue;
                        }
                    }
                });
            }
        }

        if (total === 0 || isNaN(total)) {
            return undefined;
        }

        return total;
    }

    /**
     * Item Rows / Old allocation bag
     */
    let cols: string[][] = [];
    const size: string = data.size;
    if (data.allocations && data.allocations.current) {
        cols = data.allocations.current.r.map((r) => r.c);
    } else if (data.bulks && data.bulks.r) {
        cols = data.bulks.r.map((r) => r.c);
    } else if (data.bulks && data.bulks.current && data.bulks.current.r) {
        cols = data.bulks.current.r.map((r) => r.c);
    }

    let total = 0;

    cols.forEach((col) => {
        const { bulkCount, shouldSkip } = getMetricValuesFromOldBagData(deliveryId, doorsMap, col, useSize, size);

        if (shouldSkip) {
            return;
        }

        total += bulkCount;
    });

    if (total === 0 || !total) {
        return undefined;
    }
    return total;
};
